import { Search, Filter } from 'lucide-react';

interface LeadFiltersProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  categoryFilter: string;
  onCategoryFilterChange: (value: string) => void;
  categories: string[];
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  budgetMin: string;
  budgetMax: string;
  onBudgetMinChange: (value: string) => void;
  onBudgetMaxChange: (value: string) => void;
  zipCode: string;
  onZipCodeChange: (value: string) => void;
  onApplyPreset: (preset: string) => void;
  onClearFilters: () => void;
}

export function LeadFilters({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  categories,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  budgetMin,
  budgetMax,
  onBudgetMinChange,
  onBudgetMaxChange,
  zipCode,
  onZipCodeChange,
  onApplyPreset,
  onClearFilters,
}: LeadFiltersProps) {
  return (
    <div
      className="all-leads-filters"
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "10px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        padding: "16px",
        marginBottom: "16px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              backgroundColor: "#eff6ff",
              padding: "8px",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Filter style={{ width: "16px", height: "16px", color: "#2563eb" }} />
          </div>
          <h3
            style={{
              fontSize: "14px",
              fontWeight: 500,
              color: "#111827",
              margin: 0,
            }}
          >
            Filters
          </h3>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "12px", color: "#6b7280" }}>Quick Presets:</span>

          <button
            onClick={() => onApplyPreset("today")}
            style={{
              padding: "4px 12px",
              border: "0px",
              fontSize: "12px",
              backgroundColor: "#eff6ff",
              color: "#1d4ed8",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            Today
          </button>

          <button
            onClick={() => onApplyPreset("week")}
            style={{
              padding: "4px 12px",
              border: "0px",
              fontSize: "12px",
              backgroundColor: "#f5f3ff",
              color: "#6d28d9",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            This Week
          </button>

          <button
            onClick={() => onApplyPreset("month")}
            style={{
              padding: "4px 12px",
              border: "0px",
              fontSize: "12px",
              backgroundColor: "#ecfdf5",
              color: "#15803d",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            This Month
          </button>

          <button
            onClick={() => onApplyPreset("high-value")}
            style={{
              padding: "4px 12px",
              border: "0px",
              fontSize: "12px",
              backgroundColor: "#fffbeb",
              color: "#b45309",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            High Value ($5K+)
          </button>

          <button
            onClick={onClearFilters}
            style={{
              padding: "4px 12px",
              border: "0px",
              fontSize: "12px",
              backgroundColor: "#f3f4f6",
              color: "#374151",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Basic Filters */}
      <div
        className="all-leads-basic-filters"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {/* Search */}
        <div style={{ position: "relative" }}>
          <Search
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "16px",
              height: "16px",
              color: "#9ca3af",
            }}
          />
          <input
            type="text"
            placeholder="Search by ID, title, homeowner..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{
              width: "100%",
              paddingLeft: "36px",
              paddingRight: "12px",
              paddingTop: "8px",
              paddingBottom: "8px",
              fontSize: "14px",
              border: "1px solid #d1d5db",
              borderRadius: "10px",
              outline: "none",
              transition: "all 0.2s ease",
            }}
          />
        </div>

        {/* Status Filter */}
        <div>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid #d1d5db",
              borderRadius: "10px",
              outline: "none",
              backgroundColor: "#ffffff",
              transition: "all 0.2s ease",
              cursor: "pointer",
            }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="declined">Declined</option>
            <option value="assigned">Assigned</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {/* Category Filter */}
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => onCategoryFilterChange(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid #d1d5db",
              borderRadius: "10px",
              outline: "none",
              backgroundColor: "#ffffff",
              transition: "all 0.2s ease",
              cursor: "pointer",
            }}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Advanced Filters */}
      <div
        style={{
          paddingTop: "16px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <p
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#6b7280",
            textTransform: "uppercase",
            marginBottom: "12px",
            marginTop: 0,
          }}
        >
          Advanced Filters
        </p>

        <div
          className="all-leads-advanced-filters"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "12px",
          }}
        >
          {/* Date From */}
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#4b5563", marginBottom: "4px" }}>
              Date From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                outline: "none",
              }}
            />
          </div>

          {/* Date To */}
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#4b5563", marginBottom: "4px" }}>
              Date To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                outline: "none",
              }}
            />
          </div>

          {/* Min Budget */}
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#4b5563", marginBottom: "4px" }}>
              Min Budget
            </label>
            <input
              type="number"
              placeholder="Min $"
              value={budgetMin}
              onChange={(e) => onBudgetMinChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                outline: "none",
              }}
            />
          </div>

          {/* Max Budget */}
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#4b5563", marginBottom: "4px" }}>
              Max Budget
            </label>
            <input
              type="number"
              placeholder="Max $"
              value={budgetMax}
              onChange={(e) => onBudgetMaxChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                outline: "none",
              }}
            />
          </div>

          {/* Zip Code */}
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#4b5563", marginBottom: "4px" }}>
              Zip Code
            </label>
            <input
              type="text"
              placeholder="Enter zip code"
              value={zipCode}
              onChange={(e) => onZipCodeChange(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                fontSize: "14px",
                border: "1px solid #d1d5db",
                borderRadius: "10px",
                outline: "none",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
